/**
 * One ministry: who ran it, what it bought, and what buys beneath it.
 *
 * The list page used to reveal ministers in a table under the whole
 * table, which meant selecting a row scrolled the answer out of sight and
 * left no room for the subordinate bodies at all — they showed only as a
 * count. Every other entity in this app has a dossier, and a ministry has
 * more to say than most: a portfolio, a run of cabinets, an agency
 * hierarchy, and procurement in two places at once.
 *
 * The two procurement figures are kept apart on purpose. A ministry's own
 * contracts and its agencies' contracts are different facts about who
 * signed, and summing them into one number is how Агенция „Пътна
 * инфраструктура"'s 11.1bn comes to read as МРРБ's.
 */

import { useQuery } from '@tanstack/react-query';
import { graphql } from '../gql';
import { request } from '../lib/graphql';
import { DataTable } from '../components/DataTable';
import {
  Amount, Badge, Breadcrumb, DetailHead, ErrorState, Fact, Link, Loading, SectionHead,
} from '../components/ui';
import { num, truncate } from '../lib/format';
import { PORTFOLIO_LABELS } from './Ministries';

const MINISTRY = graphql(`
  query MinistryDossier($uic: ID!) {
    ministries {
      uic name portfolio
      tenders value units unitTenders unitValue ministers cabinets
    }
    ministerTerms(uic: $uic, limit: 200) {
      personHash name portfolio govId validFrom validTo party verified
    }
    ministryUnits(uic: $uic) { uic name tenders value }
  }
`);

export function MinistryPage({ uic }: Readonly<{ uic: string }>) {
  const { data, isPending, isError, error } = useQuery({
    queryKey: ['ministry', uic],
    queryFn: () => request(MINISTRY, { uic }),
  });

  if (isPending) return <Loading variant="cards" rows={5} />;
  if (isError) return <ErrorState message={error.message} />;

  const ministry = data.ministries.find((m) => m.uic === uic);
  if (!ministry) {
    return <ErrorState message="Няма такова министерство в обхвата на сайта." />;
  }
  const label = PORTFOLIO_LABELS[ministry.portfolio] ?? ministry.portfolio;
  const terms = data.ministerTerms;
  const units = data.ministryUnits;
  const sitting = terms.find((t) => !t.validTo);

  return (
    <>
      <Breadcrumb>{[
        <Link key="root" href="#/ministries">Министерства</Link>,
        label,
      ]}</Breadcrumb>

      <DetailHead
        eyebrow="Министерство"
        title={label}
        compact
        meta={[
          ['ЕИК', <span className="mono">{ministry.uic}</span>] as const,
          // The register name, second and small: it is often the string a
          // procurement notice used rather than the ministry's own title.
          ministry.name ? ['По регистър', truncate(ministry.name, 52)] as const : null,
          sitting?.name ? ['Министър', (
            <Link href={`#/people/${sitting.personHash}`}>{sitting.name}</Link>
          )] as const : null,
        ]}
      />

      <div className="grid grid-4">
        <Fact value={<Amount value={ministry.value} />} label="Собствено възложени" />
        <Fact value={num(ministry.tenders)} label="Брой поръчки" />
        <Fact value={<Amount value={ministry.unitValue} />} label="Възложени чрез структури" />
        <Fact value={num(ministry.units)} label="Подчинени структури" />
        <Fact value={num(ministry.ministers)} label="Министри" />
        <Fact value={num(ministry.cabinets)} label="Кабинета" />
      </div>

      <section className="block">
        <SectionHead
          title="Подчинени структури"
          aside="агенции, дирекции и поделения · по възложено"
        />
        <p className="page-lede" style={{ marginBottom: '1rem' }}>
          {'Структурите се извеждат от кода по БУЛСТАТ: кодът на поделение е кодът '
           + 'на принципала плюс четири цифри. Затова списъкът съдържа само '
           + 'структури, чийто код сочи към това министерство — агенция с '
           + 'независим код се води отделно, дори да е на негово подчинение.'}
        </p>
        <DataTable
          rows={units}
          rowKey={(row) => row.uic}
          empty="Няма установени подчинени структури с код, сочещ към това министерство."
          columns={[
            {
              key: 'name',
              label: 'Структура',
              render: (row) => (
                <div>
                  <Link href={`#/companies/${row.uic}`}>
                    {truncate(row.name ?? row.uic, 54)}
                  </Link>
                  <div className="uic mono">{row.uic}</div>
                </div>
              ),
            },
            {
              key: 'tenders',
              label: 'Поръчки',
              num: true,
              render: (row) => num(row.tenders),
            },
            {
              key: 'value',
              label: 'Възложено',
              num: true,
              render: (row) => <Amount value={row.value} />,
            },
          ]}
        />
      </section>

      <section className="block">
        <SectionHead title="Кой е ръководил" aside="най-новите първи · датите са на кабинета" />
        <p className="page-lede" style={{ marginBottom: '1rem' }}>
          {'Съставът се чете от статията на всеки кабинет и се сверява с Wikidata. '
           + '„Потвърден“ означава, че вторият източник вписва същото лице в същия '
           + 'портфейл за застъпващ се период. Липсата на потвърждение най-често '
           + 'означава, че Wikidata не води този портфейл — не че записът е спорен. '
           + 'Квотата е партията, чието място заема министърът, когато статията я '
           + 'посочва; служебните кабинети нямат такава.'}
        </p>
        <DataTable
          rows={terms}
          rowKey={(row, index) => `${row.govId}-${row.personHash}-${index}`}
          empty="Няма записани министри за това министерство."
          columns={[
            {
              key: 'name',
              label: 'Министър',
              render: (row) => (
                <span className="cell-title">
                  <Link href={`#/people/${row.personHash}`}>{row.name ?? '—'}</Link>
                  {row.verified && <Badge kind="record">потвърден</Badge>}
                </span>
              ),
            },
            {
              key: 'validFrom',
              label: 'Мандат',
              render: (row) => (
                <span className="mono">
                  {`${row.validFrom ?? '—'} → ${row.validTo ?? 'сега'}`}
                </span>
              ),
            },
            {
              key: 'party',
              label: 'Квота',
              render: (row) => (row.party
                ? <Badge kind="party">{row.party}</Badge>
                : <span className="muted">—</span>),
            },
            {
              key: 'govId',
              label: 'Кабинет',
              render: (row) => <span className="mono">{row.govId}</span>,
            },
          ]}
        />
      </section>
    </>
  );
}
